import { useEffect, useMemo, useRef, useState } from "react";
import {
  CartesianGrid, Line, LineChart, ReferenceArea, ReferenceLine, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";
import { api, ApiError } from "../api";
import { tg } from "../telegram";
import { useT } from "../i18n";
import { useTelegramBackButton } from "../hooks/useTelegramBackButton";
import { nf, useBandVars, useNorms } from "../hooks/useNorms";
import type {
  AlarmEvent, AlarmLeaderboard, AlarmSummary, AlarmTimeline,
  AutoSeries, CombinedDetail, CombinedKpi, CombinedSpikes, CombinedSpikeItem,
  VBandOut,
} from "../types";

// Colors per source — same identity across leaderboard rows and drill charts.
const SRC_COLOR: Record<string, string> = {
  G1: "#2481cc", G2: "#ff9500",
};
const fmtInt = (n: number | null | undefined) => (n == null ? "—" : n.toLocaleString("ru-RU"));
const fmtWin = (iso: string) => iso.slice(5, 16).replace("T", " ");
const fmtSec = (iso: string) => iso.slice(11, 19);
const fmtDate = (iso: string | null) => (iso ? iso.slice(5) : "");

// Date + time, styled and never wrapping: bold date, muted time, tabular digits.
function DateTimeTag({ ts, size = 13 }: { ts: string; size?: number }) {
  return (
    <span style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", fontSize: size, lineHeight: 1.35, color: "var(--text)" }}>
      <span style={{ fontWeight: 700 }}>{ts.slice(0, 10)}</span>
      <span style={{ color: "var(--hint)", fontWeight: 500, marginLeft: 6 }}>{ts.slice(11, 19)}</span>
    </span>
  );
}

// «Общая» — unified view over ALL per-second sources (SCADA + G1 + G2):
// leaderboard of 5-min voltage-spike windows + summary KPI. Click a bar to open
// a per-second RS/ST/RT drill of that window ±10 s.
// Source → which per-second API + unit backs the Разрез drill table.
const DRILL_SOURCES: { key: string; api: string; unit: number }[] = [
  { key: "G1", api: "/api/scada_v2", unit: 1 },
  { key: "G2", api: "/api/scada_v2", unit: 2 },
];
const MM_DRILL = [60, 5, 1, 0];   // minutes → 0 = per-second
const MM_LINES = [{ key: "voltage_rs", name: "RS" }, { key: "voltage_st", name: "ST" }, { key: "voltage_rt", name: "RT" }];

export function OverviewSub({ onErr, openDetail, showKpi = true }: {
  onErr: (m: string) => void;
  openDetail: (d: { src: string; win?: string; at?: string }) => void;
  showKpi?: boolean;
}) {
  const { t, lang } = useT();
  const norms = useNorms();
  const [kpi, setKpi] = useState<CombinedKpi | null>(null);
  const [sharp, setSharp] = useState<CombinedSpikes | null>(null);
  const [prolonged, setProlonged] = useState<CombinedSpikes | null>(null);

  useEffect(() => {
    setKpi(null); setSharp(null); setProlonged(null);
    api.get<CombinedKpi>("/api/combined/kpi").then(setKpi).catch((e: ApiError) => onErr(e.message));
    api.get<CombinedSpikes>("/api/combined/spikes?kind=sharp&limit=15").then(setSharp).catch(() => undefined);
    api.get<CombinedSpikes>("/api/combined/spikes?kind=prolonged&limit=15").then(setProlonged).catch(() => undefined);
  }, [onErr]);

  return (
    <div>

      {/* KPI summary tiles (per-source + totals) */}
      <div className="auto-tiles" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
        <div className="auto-tile kw">
          <div className="auto-tile-top"><span className="auto-tile-icon">⚡</span>
            <span className="auto-tile-title">{t("combined.max_kw")}</span></div>
          <div className="auto-tile-bigrow">
            <span className="auto-tile-big">{fmtInt(kpi?.max_kw)}</span>
            <span className="auto-tile-unit">кВт</span>
          </div>
        </div>
        <div className="auto-tile energy">
          <div className="auto-tile-top"><span className="auto-tile-icon">🔋</span>
            <span className="auto-tile-title">{t("combined.total_energy")}</span></div>
          <div className="auto-tile-bigrow">
            <span className="auto-tile-big">
              {kpi && kpi.total_energy_kwh >= 1000
                ? (kpi.total_energy_kwh / 1000).toFixed(1)
                : fmtInt(kpi?.total_energy_kwh)}
            </span>
            <span className="auto-tile-unit">
              {kpi && kpi.total_energy_kwh >= 1000 ? "МВт·ч" : "кВт·ч"}
            </span>
          </div>
        </div>
        <div className="auto-tile">
          <div className="auto-tile-top"><span className="auto-tile-icon">🕒</span>
            <span className="auto-tile-title">{t("combined.total_run")}</span></div>
          <div className="auto-tile-bigrow">
            <span className="auto-tile-big">
              {kpi ? (kpi.total_run_min / 60).toFixed(1) : "—"}
            </span>
            <span className="auto-tile-unit">ч</span>
          </div>
        </div>
      </div>

      {/* Per-source breakdown row */}
      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>{t("combined.by_source")}</div>
        {!kpi && <div className="empty-state"><span className="spinner" /></div>}
        {kpi && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 6 }}>
            {kpi.per_source.map((s) => (
              <div key={s.src} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
                <span style={{
                  minWidth: 52, textAlign: "center", padding: "2px 8px",
                  background: SRC_COLOR[s.src] || "var(--hint)", color: "white",
                  borderRadius: 8, fontWeight: 700, fontSize: 12,
                }}>{s.src}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: "var(--hint)" }}>
                    {fmtDate(s.d_from)} → {fmtDate(s.d_to)} · {s.v_lo}–{s.v_hi} кВ ·{" "}
                    {((s.run_min ?? 0) / 60).toFixed(1)} ч
                  </div>
                </div>
                <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  <div style={{ fontWeight: 700 }}>{fmtInt(s.max_kw)} кВт</div>
                  <div style={{ fontSize: 11, color: "var(--hint)" }}>{fmtInt(s.energy_kwh)} кВт·ч</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Leaderboards */}
      <SpikeSection title={t("combined.sharp_title")} hint={t("combined.sharp_hint", { kv: nf(norms.sharp_kv, lang) })}
        data={sharp} onSelect={(it) => { tg()?.HapticFeedback?.impactOccurred("light"); openDetail({ src: it.src, win: it.win }); }} />
      <SpikeSection title={t("combined.prolonged_title")} hint={t("combined.prolonged_hint", { lo: nf(norms.v_lo, lang), hi: nf(norms.v_hi, lang), s: nf(norms.prolonged_s, lang) })}
        data={prolonged} onSelect={(it) => { tg()?.HapticFeedback?.impactOccurred("light"); openDetail({ src: it.src, win: it.win }); }} />
    </div>
  );
}

// A single leaderboard section: horizontal bars per 5-min window, longest first,
// with a source badge and value/peak. Click → drill into per-second detail.
function SpikeSection({ title, hint, data, onSelect }: {
  title: string; hint: string; data: CombinedSpikes | null; onSelect: (it: CombinedSpikeItem) => void;
}) {
  const max = useMemo(() => (data?.items ?? []).reduce((m, x) => Math.max(m, x.value), 0), [data]);
  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 11, color: "var(--hint)", marginBottom: 8 }}>{hint}</div>
      {!data && <div className="empty-state"><span className="spinner" /></div>}
      {data && data.items.length === 0 && <div className="empty-state">—</div>}
      {data && data.items.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {data.items.map((it, i) => {
            const pct = max > 0 ? Math.max(6, Math.round((it.value / max) * 100)) : 6;
            const color = SRC_COLOR[it.src] || "var(--link)";
            return (
              <div key={i} onClick={() => onSelect(it)}
                style={{ cursor: "pointer", padding: "4px 2px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                  <span style={{
                    minWidth: 48, textAlign: "center", padding: "1px 6px",
                    background: color, color: "white", borderRadius: 6, fontWeight: 700, fontSize: 11,
                  }}>{it.src}</span>
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{fmtWin(it.win)}</span>
                  <span style={{ fontWeight: 700, fontSize: 14, color }}>
                    {fmtInt(it.value)} <span style={{ fontSize: 10, color: "var(--hint)", fontWeight: 400 }}>{data.unit}</span>
                  </span>
                </div>
                <div style={{
                  height: 6, borderRadius: 6, background: "var(--bg2, rgba(255,255,255,0.08))",
                  overflow: "hidden",
                }}>
                  <div style={{
                    height: "100%", width: pct + "%",
                    background: `linear-gradient(90deg, ${color}, ${color}bb)`,
                    borderRadius: 6,
                  }} />
                </div>
                <div style={{ marginTop: 3, fontSize: 11, color: "var(--hint)" }}>
                  {it.peak != null && <>пик Δ {it.peak.toFixed(1)} кВ · </>}
                  {it.nruns != null && <>{it.nruns} серий · </>}
                  тап → посекундно ›
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Per-second detail for one 5-min window (±10 s). RS/ST/RT lines + red dots on
// sharp seconds + 19/21 kV reference band, so it's clear what the spike looked
// like and how the signal behaved right after.
export function DrillModal({ src, win, at, onClose }: {
  src: string; win?: string; at?: string; onClose: () => void;
}) {
  const { t } = useT();
  useTelegramBackButton(onClose, [onClose]);
  const [data, setData] = useState<CombinedDetail | null>(null);
  useEffect(() => {
    const p = new URLSearchParams({ src });
    if (at) p.set("at", at); else if (win) p.set("win", win);
    api.get<CombinedDetail>(`/api/combined/detail?${p.toString()}`)
      .then(setData).catch(() => undefined);
  }, [src, win, at]);

  const rows = useMemo(() => (data?.points ?? []).map((p) => ({
    label: fmtSec(p.t), full: p.t.slice(0, 19).replace("T", " "),
    rs: p.rs, st: p.st, rt: p.rt, kw: p.kw, hz: p.hz, sharp: p.sharp, oob: p.oob,
  })), [data]);
  const nSharp = (data?.points ?? []).filter((p) => p.sharp).length;
  const nOob = (data?.points ?? []).filter((p) => p.oob).length;
  // All-zero window = the unit was stopped at that moment (still informative).
  const allZero = rows.length > 0 && rows.every((r) => !(r.st || r.kw || r.rs));
  const atLabel = at ? fmtSec(at) : null;
  // "unit was off" points (all line voltages 0) are dropped from the table —
  // such empty data shouldn't clutter it. The event second (◆) is always kept.
  const tableRows = (data?.points ?? []).filter((p) => {
    const isAt = at != null && p.t.slice(0, 19) === at.slice(0, 19);
    return isAt || !!(p.rs || p.st || p.rt);
  });
  // Per-line min / max across the shown seconds — summary footer for the table.
  const mm = (k: "rs" | "st" | "rt" | "kw" | "hz") => {
    const xs = tableRows.map((p) => p[k]).filter((x): x is number => x != null);
    return xs.length ? ([Math.min(...xs), Math.max(...xs)] as const) : null;
  };
  const MM_COLS = [
    { key: "rs", d: 1 }, { key: "st", d: 1 }, { key: "rt", d: 1 }, { key: "kw", d: 0 }, { key: "hz", d: 1 },
  ] as const;
  const color = SRC_COLOR[src] || "var(--link)";
  const title = at ? (at.slice(5, 19).replace("T", " ")) : (win ? fmtWin(win) : "");

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 200,
      display: "flex", alignItems: "flex-end",
    }} onClick={onClose}>
      <div style={{
        background: "var(--bg)", width: "100%", maxHeight: "92vh", overflowY: "auto",
        borderRadius: "16px 16px 0 0", padding: 16,
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <span style={{
            padding: "2px 10px", background: color, color: "white",
            borderRadius: 8, fontWeight: 700, fontSize: 12,
          }}>{src}</span>
          <h2 style={{ margin: 0, fontSize: 16 }}>{title}</h2>
        </div>
        <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 10 }}>
          {at
            ? t("combined.event_hint").replace("{at}", atLabel || "")
            : t("combined.detail_hint").replace("{sharp}", String(nSharp)).replace("{oob}", String(nOob))}
        </div>
        {!data && <div className="empty-state"><span className="spinner" /></div>}
        {data && rows.length === 0 && <div className="empty-state">{t("combined.no_telemetry")}</div>}
        {allZero && (
          <div className="alert" style={{ marginBottom: 10, background: "var(--bg2)", padding: "8px 12px", borderRadius: 8 }}>
            🛑 {t("combined.unit_stopped")}
          </div>
        )}
        {data && rows.length > 0 && !allZero && (
          <>
            {/* Voltage RS/ST/RT (left kV) */}
            <ResponsiveContainer width="100%" height={230}>
              <LineChart data={rows} margin={{ top: 8, right: 10, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                <XAxis dataKey="label" tick={{ fontSize: 9 }} minTickGap={40} />
                <YAxis tick={{ fontSize: 10 }} width={40} domain={["auto", "auto"]} />
                <Tooltip
                  contentStyle={{ background: "var(--bg2)", border: "1px solid var(--border)", fontSize: 12 }}
                  labelFormatter={(_l, p) => (p && p[0] ? (p[0].payload as { full: string }).full : "")}
                  formatter={(v: unknown) => (typeof v === "number" ? v.toFixed(2) : String(v ?? ""))}
                />
                <ReferenceArea y1={data.band[0]} y2={data.band[1]} fill="#34c759" fillOpacity={0.08}
                  ifOverflow="extendDomain" />
                <ReferenceLine y={data.band[0]} stroke="#34c759" strokeDasharray="4 3" ifOverflow="extendDomain" />
                <ReferenceLine y={data.band[1]} stroke="#34c759" strokeDasharray="4 3" ifOverflow="extendDomain" />
                {atLabel && <ReferenceLine x={atLabel} stroke="#ff453a" strokeWidth={1.5}
                  label={{ value: "◆", position: "top", fill: "#ff453a", fontSize: 12 }} />}
                <Line type="monotone" dataKey="rs" name="RS" stroke="#2481cc" strokeWidth={1.4}
                  dot={(props) => {
                    const r = props.payload as { sharp: boolean };
                    return r?.sharp
                      ? <circle key={props.key} cx={props.cx} cy={props.cy} r={3.5} fill="#ff453a" stroke="none" />
                      : <g key={props.key} />;
                  }}
                  isAnimationActive={false} />
                <Line type="monotone" dataKey="st" name="ST" stroke="#34c759" strokeWidth={1.4}
                  dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="rt" name="RT" stroke="#ff9500" strokeWidth={1.4}
                  dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
            <div style={{ fontSize: 11, color: "var(--hint)", margin: "4px 0 6px", textAlign: "center" }}>
              RS / ST / RT, кВ
            </div>
            {/* Power (kW) + frequency (Hz), dual axis */}
            <ResponsiveContainer width="100%" height={170}>
              <LineChart data={rows} margin={{ top: 4, right: 6, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                <XAxis dataKey="label" tick={{ fontSize: 9 }} minTickGap={40} />
                <YAxis yAxisId="kw" tick={{ fontSize: 9 }} width={40} domain={["auto", "auto"]} />
                <YAxis yAxisId="hz" orientation="right" tick={{ fontSize: 9, fill: "#e3350d" }} width={30} domain={["auto", "auto"]} />
                <Tooltip
                  contentStyle={{ background: "var(--bg2)", border: "1px solid var(--border)", fontSize: 12 }}
                  labelFormatter={(_l, p) => (p && p[0] ? (p[0].payload as { full: string }).full : "")} />
                {atLabel && <ReferenceLine yAxisId="kw" x={atLabel} stroke="#ff453a" strokeWidth={1.5} />}
                <Line yAxisId="kw" type="monotone" dataKey="kw" name="кВт" stroke="#2481cc" strokeWidth={1.4} dot={false} isAnimationActive={false} />
                <Line yAxisId="hz" type="monotone" dataKey="hz" name="Гц" stroke="#e3350d" strokeWidth={1.2} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
            <div style={{ fontSize: 11, color: "var(--hint)", margin: "4px 0 6px", textAlign: "center" }}>
              <span style={{ color: "#2481cc" }}>кВт</span> · <span style={{ color: "#e3350d" }}>Гц</span>
              {atLabel && <> · <span style={{ color: "#ff453a" }}>◆ {t("combined.moment")}</span></>}
            </div>
            {/* Per-second data table — off/zero seconds dropped */}
            <div style={{ marginTop: 8, overflowX: "auto" }}>
              <table className="table" style={{ fontSize: 12 }}>
                <thead><tr>
                  <th>{t("combined.col_time")}</th><th>RS</th><th>ST</th><th>RT</th><th>кВт</th><th>Гц</th>
                </tr></thead>
                <tbody>
                  {tableRows.map((p) => {
                    const isAt = at != null && p.t.slice(0, 19) === at.slice(0, 19);
                    return (
                      <tr key={p.t} style={
                        isAt ? { background: "rgba(255,69,58,0.22)", fontWeight: 700 }
                          : p.sharp ? { background: "rgba(255,69,58,0.10)" } : undefined}>
                        <td style={{ whiteSpace: "nowrap" }}>{isAt ? "◆ " : ""}{fmtSec(p.t)}</td>
                        <td className="num" style={p.oob ? { color: "#ff453a" } : undefined}>{p.rs?.toFixed(1) ?? "—"}</td>
                        <td className="num" style={p.oob ? { color: "#ff453a" } : undefined}>{p.st?.toFixed(1) ?? "—"}</td>
                        <td className="num" style={p.oob ? { color: "#ff453a" } : undefined}>{p.rt?.toFixed(1) ?? "—"}</td>
                        <td className="num">{p.kw != null ? Math.round(p.kw) : "—"}</td>
                        <td className="num">{p.hz != null ? p.hz.toFixed(1) : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  {(["min", "max"] as const).map((which, wi) => (
                    <tr key={which} style={wi === 0 ? { borderTop: "2px solid rgba(255,255,255,0.16)" } : undefined}>
                      <td style={{ fontWeight: 700, color: "var(--hint)" }}>{t("minmax." + which)}</td>
                      {MM_COLS.map((c) => {
                        const r = mm(c.key);
                        const v = r ? r[wi] : null;
                        return <td key={c.key} className="num" style={{ fontWeight: 700 }}>{v == null ? "—" : v.toFixed(c.d)}</td>;
                      })}
                    </tr>
                  ))}
                </tfoot>
              </table>
            </div>
          </>
        )}
        <button className="btn" style={{ marginTop: 12 }} onClick={onClose}>{t("common.close")}</button>
      </div>
    </div>
  );
}

// ============ Коридор 19–21 кВ (voltage band) sub-tab ============

const VB_PHASE = [
  { key: "r", name: "R (RS)", color: "#2481cc" },
  { key: "s", name: "S (ST)", color: "#e3350d" },
  { key: "t", name: "T (RT)", color: "#ff9500" },
];
const VB_ABOVE = "#ff2d55";  // > 21 kV
const VB_BELOW = "#5e5ce6";  // < 19 kV
const fmtAxis = (iso: string) => iso.slice(5, 16).replace("T", " ");

// R/S/T tracked against the 19–21 kV corridor over the whole period: the three
// phase lines stay inside the green tunnel, every excursion is drawn as its own
// coloured series (above / below) and listed as a clickable episode.
export function VBandSub({ onErr, openDetail }: {
  onErr: (m: string) => void;
  openDetail: (d: { src: string; win?: string; at?: string }) => void;
}) {
  const { t, lang } = useT();
  const norms = useNorms();
  const [srcKey, setSrcKey] = useState("G2");
  const [range, setRange] = useState<"all" | "7d" | "24h">("all");
  const [meta, setMeta] = useState<{ from: string; to: string } | null>(null);
  const [data, setData] = useState<VBandOut | null>(null);

  useEffect(() => { setMeta(null); setRange("all"); }, [srcKey]);

  useEffect(() => {
    setData(null);
    const p = new URLSearchParams({ src: srcKey, bucket: "auto" });
    if (range !== "all" && meta) {
      const end = new Date(meta.to).getTime();
      const hours = range === "7d" ? 168 : 24;
      p.set("from", new Date(end - hours * 3600_000).toISOString());
      p.set("to", meta.to);
    }
    api.get<VBandOut>(`/api/combined/vband?${p.toString()}`)
      .then((d) => {
        setData(d);
        if (range === "all" && d.from && d.to
            && (meta?.from !== d.from || meta?.to !== d.to)) {
          setMeta({ from: d.from, to: d.to });
        }
      })
      .catch((e: ApiError) => onErr(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srcKey, range, meta?.to]);

  const rows = useMemo(() => (data?.points ?? []).map((p) => ({
    ...p, label: fmtAxis(p.t), full: p.t.slice(0, 19).replace("T", " "),
  })), [data]);

  const st = data?.stats;
  const lo = data?.band[0] ?? norms.v_lo;
  const hi = data?.band[1] ?? norms.v_hi;
  const pct = (x: number | null | undefined) => (x == null ? "—" : x.toFixed(1) + " %");
  const kv = (x: number | null | undefined) => (x == null ? "—" : x.toFixed(2));

  return (
    <div>
      {/* source + period */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "var(--hint)" }}>{t("combined.drill_source")}:</span>
          {DRILL_SOURCES.map((s) => (
            <button key={s.key} onClick={() => { tg()?.HapticFeedback?.selectionChanged?.(); setSrcKey(s.key); }}
              style={{
                padding: "4px 12px", borderRadius: 8, border: "1px solid var(--border)",
                background: srcKey === s.key ? (SRC_COLOR[s.key] || "var(--link)") : "transparent",
                color: srcKey === s.key ? "white" : "var(--text)", fontWeight: 700, fontSize: 12, cursor: "pointer",
              }}>{s.key}</button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
          <span style={{ fontSize: 12, color: "var(--hint)" }}>{t("vband.period")}:</span>
          {([["all", t("vband.all")], ["7d", "7 " + t("vband.days")], ["24h", "24 " + t("vband.hours")]] as const).map(([k, lb]) => (
            <button key={k} disabled={k !== "all" && !meta}
              onClick={() => { tg()?.HapticFeedback?.selectionChanged?.(); setRange(k as "all" | "7d" | "24h"); }}
              style={{
                padding: "4px 12px", borderRadius: 8, border: "1px solid var(--border)",
                background: range === k ? "var(--link, #2481cc)" : "transparent",
                color: range === k ? "white" : "var(--text)", fontWeight: 600, fontSize: 12,
                cursor: k !== "all" && !meta ? "default" : "pointer", opacity: k !== "all" && !meta ? 0.5 : 1,
              }}>{lb}</button>
          ))}
        </div>
        {data?.from && (
          <div style={{ fontSize: 11, color: "var(--hint)", marginTop: 8 }}>
            {t("vband.data")}: <b style={{ color: "var(--text)", fontVariantNumeric: "tabular-nums" }}>{data.from.slice(0, 16).replace("T", " ")}</b>
            {" → "}<b style={{ color: "var(--text)", fontVariantNumeric: "tabular-nums" }}>{data.to?.slice(0, 16).replace("T", " ")}</b>
            {data.bucket_min ? ` · ${t("vband.bucket")} ${data.bucket_min} ${t("auto.min_short")}` : ""}
          </div>
        )}
      </div>

      {!data && <div className="empty-state"><span className="spinner" /></div>}

      {data && st && (
        <>
          {/* KPI */}
          <div className="auto-tiles" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
            <div className="auto-tile">
              <div className="auto-tile-top"><span className="auto-tile-icon">🎯</span>
                <span className="auto-tile-title">{t("vband.in_band")}</span></div>
              <div className="auto-tile-bigrow">
                <span className="auto-tile-big" style={{ color: (st.pct_in ?? 100) >= 99 ? "#34c759" : undefined }}>
                  {pct(st.pct_in)}
                </span>
              </div>
              <div style={{ fontSize: 10.5, color: "var(--hint)" }}>
                {fmtInt(st.in_band)} / {fmtInt(st.minutes)} {t("vband.min_unit")}
              </div>
            </div>
            <div className="auto-tile" style={st.above > 0 ? { border: "1.6px solid " + VB_ABOVE } : undefined}>
              <div className="auto-tile-top"><span className="auto-tile-icon">🔺</span>
                <span className="auto-tile-title">{t("vband.above")} {hi} кВ</span></div>
              <div className="auto-tile-bigrow">
                <span className="auto-tile-big" style={st.above > 0 ? { color: VB_ABOVE } : undefined}>{fmtInt(st.above)}</span>
                <span className="auto-tile-unit">{t("vband.min_unit")}</span>
              </div>
              <div style={{ fontSize: 10.5, color: "var(--hint)" }}>max {kv(st.v_max)} кВ</div>
            </div>
            <div className="auto-tile" style={st.below > 0 ? { border: "1.6px solid " + VB_BELOW } : undefined}>
              <div className="auto-tile-top"><span className="auto-tile-icon">🔻</span>
                <span className="auto-tile-title">{t("vband.below")} {lo} кВ</span></div>
              <div className="auto-tile-bigrow">
                <span className="auto-tile-big" style={st.below > 0 ? { color: VB_BELOW } : undefined}>{fmtInt(st.below)}</span>
                <span className="auto-tile-unit">{t("vband.min_unit")}</span>
              </div>
              <div style={{ fontSize: 10.5, color: "var(--hint)" }}>min {kv(st.v_min)} кВ</div>
            </div>
          </div>

          {/* corridor chart */}
          <div className="card" style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 2 }}>{t("vband.chart_title", { lo: nf(lo, lang), hi: nf(hi, lang) })}</div>
            <div style={{ fontSize: 11, color: "var(--hint)", marginBottom: 6 }}>{t("vband.chart_hint", { lo: nf(lo, lang), hi: nf(hi, lang) })}</div>
            {rows.length === 0 && <div className="empty-state">{t("common.no_data")}</div>}
            {rows.length > 0 && (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={rows} margin={{ top: 8, right: 10, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                  <XAxis dataKey="label" tick={{ fontSize: 9 }} minTickGap={55} />
                  <YAxis tick={{ fontSize: 10 }} width={42}
                    domain={[(d: number) => Math.min(lo - 0.6, d - 0.2), (d: number) => Math.max(hi + 0.6, d + 0.2)]} />
                  <Tooltip
                    contentStyle={{ background: "var(--bg2)", border: "1px solid var(--border)", fontSize: 12 }}
                    labelFormatter={(_l, p) => (p && p[0] ? (p[0].payload as { full: string }).full : "")}
                    formatter={(v: unknown, n: unknown) => [typeof v === "number" ? v.toFixed(2) + " кВ" : String(v ?? ""), String(n ?? "")]}
                  />
                  {/* the corridor itself */}
                  <ReferenceArea y1={lo} y2={hi} fill="#34c759" fillOpacity={0.13} ifOverflow="extendDomain" />
                  <ReferenceLine y={hi} stroke="#34c759" strokeDasharray="5 3" ifOverflow="extendDomain"
                    label={{ value: `${hi} кВ`, position: "right", fill: "#34c759", fontSize: 9 }} />
                  <ReferenceLine y={lo} stroke="#34c759" strokeDasharray="5 3" ifOverflow="extendDomain"
                    label={{ value: `${lo} кВ`, position: "right", fill: "#34c759", fontSize: 9 }} />
                  {VB_PHASE.map((ph) => (
                    <Line key={ph.key} type="monotone" dataKey={`${ph.key}_avg`} name={ph.name}
                      stroke={ph.color} strokeWidth={1.4} dot={false} connectNulls={false} isAnimationActive={false} />
                  ))}
                  {/* deviations — own colour, drawn as isolated markers */}
                  <Line type="monotone" dataKey="dev_hi" name={`> ${hi} кВ`} stroke="none" connectNulls={false}
                    isAnimationActive={false}
                    dot={(p) => (p.value == null
                      ? <g key={p.key} />
                      : <circle key={p.key} cx={p.cx} cy={p.cy} r={3.6} fill={VB_ABOVE} stroke="#fff" strokeWidth={0.8} />)} />
                  <Line type="monotone" dataKey="dev_lo" name={`< ${lo} кВ`} stroke="none" connectNulls={false}
                    isAnimationActive={false}
                    dot={(p) => (p.value == null
                      ? <g key={p.key} />
                      : <circle key={p.key} cx={p.cx} cy={p.cy} r={3.6} fill={VB_BELOW} stroke="#fff" strokeWidth={0.8} />)} />
                </LineChart>
              </ResponsiveContainer>
            )}
            <div style={{ fontSize: 11, color: "var(--hint)", textAlign: "center", marginTop: 2 }}>
              {VB_PHASE.map((ph, i) => (
                <span key={ph.key}>{i > 0 && " · "}<span style={{ color: ph.color, fontWeight: 700 }}>{ph.name}</span></span>
              ))}
              {" · "}<span style={{ color: VB_ABOVE, fontWeight: 700 }}>● &gt; {hi}</span>
              {" · "}<span style={{ color: VB_BELOW, fontWeight: 700 }}>● &lt; {lo}</span>
            </div>
          </div>

          {/* per-phase summary */}
          <div className="card" style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>{t("vband.by_phase")}</div>
            <div style={{ overflowX: "auto" }}>
              <table className="table" style={{ fontSize: 12 }}>
                <thead><tr>
                  <th>{t("vband.phase")}</th><th>min, кВ</th><th>max, кВ</th>
                  <th style={{ color: VB_ABOVE }}>&gt; {hi}</th><th style={{ color: VB_BELOW }}>&lt; {lo}</th>
                </tr></thead>
                <tbody>
                  {VB_PHASE.map((ph) => {
                    const s = st.per_phase[ph.key];
                    if (!s) return null;
                    return (
                      <tr key={ph.key}>
                        <td style={{ color: ph.color, fontWeight: 700, whiteSpace: "nowrap" }}>{ph.name}</td>
                        <td className="num" style={(s.min ?? lo) < lo ? { color: VB_BELOW, fontWeight: 700 } : undefined}>{kv(s.min)}</td>
                        <td className="num" style={(s.max ?? hi) > hi ? { color: VB_ABOVE, fontWeight: 700 } : undefined}>{kv(s.max)}</td>
                        <td className="num" style={s.above > 0 ? { color: VB_ABOVE, fontWeight: 700 } : undefined}>{fmtInt(s.above)}</td>
                        <td className="num" style={s.below > 0 ? { color: VB_BELOW, fontWeight: 700 } : undefined}>{fmtInt(s.below)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* excursion episodes */}
          <div className="card" style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 2 }}>
              {t("vband.excursions")}
              <span style={{ fontWeight: 400, color: "var(--hint)", fontSize: 12 }}>
                {" · "}{fmtInt(data.excursions.length)}{data.truncated ? "+" : ""}
              </span>
            </div>
            <div style={{ fontSize: 11, color: "var(--hint)", marginBottom: 8 }}>{t("vband.exc_hint")}</div>
            {data.excursions.length === 0 && <div className="empty-state">{t("vband.no_dev")}</div>}
            {data.excursions.length > 0 && (
              <div style={{ overflowX: "auto" }}>
                <table className="table" style={{ fontSize: 12 }}>
                  <thead><tr>
                    <th>{t("combined.col_time")}</th><th>{t("vband.dir")}</th>
                    <th>{t("vband.phase")}</th><th>{t("vband.peak")}</th>
                    <th>{t("vband.dur")}</th><th></th>
                  </tr></thead>
                  <tbody>
                    {data.excursions.slice(0, 60).map((e, i) => {
                      const col = e.dir === "above" ? VB_ABOVE : VB_BELOW;
                      const ph = VB_PHASE.find((x) => x.key === e.phase);
                      return (
                        <tr key={i} style={{ background: col + "14" }}>
                          <td style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", borderLeft: `3px solid ${col}` }}>
                            {e.from.slice(5, 16).replace("T", " ")}
                          </td>
                          <td style={{ color: col, fontWeight: 700, whiteSpace: "nowrap" }}>
                            {e.dir === "above" ? `▲ > ${hi}` : `▼ < ${lo}`}
                          </td>
                          <td style={{ color: ph?.color, fontWeight: 700 }}>{ph?.name ?? e.phase}</td>
                          <td className="num" style={{ fontWeight: 700 }}>{e.peak.toFixed(2)}</td>
                          <td className="num">{fmtInt(e.minutes)} {t("vband.min_unit")}</td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            <button className="btn secondary" style={{ padding: "2px 7px", fontSize: 11 }}
                              onClick={() => { tg()?.HapticFeedback?.impactOccurred("light"); openDetail({ src: srcKey, at: e.from }); }}>
                              📈
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ============ Разрез (Min/Max drill table) sub-tab ============

// Per-source drill table over the line voltages, 60 min → 5 → 1 → per-second,
// min/max per RS/ST/RT with out-of-band cells in red — the same "разрез" the
// SCADA/v2 tabs have, here with a source selector.
export function DrillTableSub({ onErr }: { onErr: (m: string) => void }) {
  const { t } = useT();
  const norms = useNorms();
  const bv = useBandVars();
  const [srcKey, setSrcKey] = useState("G1");
  const cfg = DRILL_SOURCES.find((s) => s.key === srcKey)!;
  const [stack, setStack] = useState<{ bucketMin: number; from?: string; to?: string }[]>([]);
  const [series, setSeries] = useState<Record<string, AutoSeries> | null>(null);

  useEffect(() => { setStack([]); }, [srcKey]);

  // Native Telegram back steps up ONE drill level (only while drilled in) —
  // otherwise the phone/Telegram back would drop the user out entirely.
  useEffect(() => {
    const tt = tg();
    if (!tt || stack.length === 0) return;
    const h = () => setStack((s) => s.slice(0, -1));
    tt.BackButton.onClick(h);
    tt.BackButton.show();
    return () => { tt.BackButton.offClick(h); tt.BackButton.hide(); };
  }, [stack.length]);

  const level = stack.length ? stack[stack.length - 1] : { bucketMin: MM_DRILL[0] };
  const isSec = level.bucketMin === 0;
  useEffect(() => {
    setSeries(null);
    Promise.all(MM_LINES.map((v) => {
      const p = new URLSearchParams({
        unit: String(cfg.unit), metric: v.key,
        bucket: isSec ? "raw" : String(level.bucketMin), points: "2000",
      });
      if (level.from) p.set("from", level.from);
      if (level.to) p.set("to", level.to);
      return api.get<AutoSeries>(`${cfg.api}/series?${p.toString()}`).then((s) => [v.key, s] as const);
    })).then((arr) => setSeries(Object.fromEntries(arr) as Record<string, AutoSeries>))
      .catch((e: ApiError) => onErr(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.api, cfg.unit, level.bucketMin, level.from, level.to]);

  type Cell = { min: number | null; max: number | null };
  const rows = useMemo(() => {
    if (!series) return [] as { t: string; cells: Record<string, Cell> }[];
    const byT = new Map<string, Record<string, Cell>>();
    for (const v of MM_LINES) {
      for (const pt of series[v.key]?.points ?? []) {
        const row = byT.get(pt.t) ?? {};
        row[v.key] = { min: pt.min, max: pt.max };
        byT.set(pt.t, row);
      }
    }
    return [...byT.entries()].map(([tt, cells]) => ({ t: tt, cells })).sort((a, b) => (a.t < b.t ? 1 : -1));
  }, [series]);

  // "unit was off" rows (all line voltages 0) — dropped entirely; such empty
  // data shouldn't appear in the drill at all.
  const rowIsZero = (r: { cells: Record<string, Cell> }) =>
    MM_LINES.every((v) => { const c = r.cells[v.key]; return !c || (!(c.min ?? 0) && !(c.max ?? 0)); });
  const visRows = rows.filter((r) => !rowIsZero(r));

  const nextBucket = (b: number) => {
    const i = MM_DRILL.indexOf(b);
    return i >= 0 && i < MM_DRILL.length - 1 ? MM_DRILL[i + 1] : null;
  };
  const isLeaf = nextBucket(level.bucketMin) == null;
  const drill = (rowT: string) => {
    const nb = nextBucket(level.bucketMin);
    if (nb == null) return;
    const to = new Date(new Date(rowT).getTime() + level.bucketMin * 60000).toISOString();
    setStack((s) => [...s, { bucketMin: nb, from: rowT, to }]);
    tg()?.HapticFeedback?.impactOccurred("medium");
  };
  const isLo = (c?: Cell) => !!c && c.min != null && c.min < norms.v_lo;
  const isHi = (c?: Cell) => !!c && c.max != null && c.max > norms.v_hi;
  const kvv = (x?: number | null) => (x == null ? "—" : x.toFixed(2));

  return (
    <div>
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "var(--hint)" }}>{t("combined.drill_source")}:</span>
          {DRILL_SOURCES.map((s) => (
            <button key={s.key} onClick={() => { tg()?.HapticFeedback?.selectionChanged?.(); setSrcKey(s.key); }}
              style={{
                padding: "4px 12px", borderRadius: 8, border: "1px solid var(--border)",
                background: srcKey === s.key ? (SRC_COLOR[s.key] || "var(--link)") : "transparent",
                color: srcKey === s.key ? "white" : "var(--text)", fontWeight: 700, fontSize: 12, cursor: "pointer",
              }}>{s.key}</button>
          ))}
        </div>
      </div>

      <div className="card">
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: "var(--hint)" }}>
            {t("minmax.bucket")}: {isSec ? t("minmax.sec") : level.bucketMin + " " + t("auto.min_short")}
          </span>
          {stack.length > 0 && (
            <button className="btn secondary" style={{ padding: "4px 10px" }}
              onClick={() => { setStack((s) => s.slice(0, -1)); tg()?.HapticFeedback?.impactOccurred("light"); }}>
              {t("auto.back")}
            </button>
          )}
          {stack.length > 0 && (
            <button className="btn secondary" style={{ padding: "4px 10px" }}
              onClick={() => { setStack([]); tg()?.HapticFeedback?.impactOccurred("light"); }}>
              {t("auto.reset_zoom")}
            </button>
          )}
        </div>
        <div style={{ fontSize: 11, color: "var(--hint)", marginBottom: 8 }}>
          {isLeaf ? t("minmax.hint_leaf", bv) : t("minmax.hint", bv)}
        </div>
        {!series && <div className="empty-state"><span className="spinner" /></div>}
        {series && visRows.length === 0 && <div className="empty-state">{t("common.no_data")}</div>}
        {series && visRows.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table className="table" style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th rowSpan={2}>{t("minmax.time")}</th>
                  {MM_LINES.map((v) => <th key={v.key} colSpan={2} style={{ textAlign: "center" }}>{v.name}</th>)}
                </tr>
                <tr>{MM_LINES.flatMap((v) => [<th key={v.key + "mn"}>{t("minmax.min")}</th>, <th key={v.key + "mx"}>{t("minmax.max")}</th>])}</tr>
              </thead>
              <tbody>
                {visRows.map((r) => {
                  const anyOut = MM_LINES.some((v) => isLo(r.cells[v.key]) || isHi(r.cells[v.key]));
                  return (
                    <tr key={r.t} onClick={() => !isLeaf && drill(r.t)}
                      style={{ cursor: isLeaf ? "default" : "pointer",
                        background: anyOut ? "rgba(255,69,58,0.10)" : undefined }}>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {isSec ? r.t.slice(11, 19) : r.t.slice(5, 16).replace("T", " ")}
                        {!isLeaf && <span style={{ color: "var(--link, #2481cc)" }}> ›</span>}
                      </td>
                      {MM_LINES.flatMap((v) => {
                        const c = r.cells[v.key];
                        return [
                          <td key={v.key + "mn"} className="num" style={{ color: isLo(c) ? "#ff453a" : undefined }}>{kvv(c?.min)}</td>,
                          <td key={v.key + "mx"} className="num" style={{ color: isHi(c) ? "#ff453a" : undefined }}>{kvv(c?.max)}</td>,
                        ];
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ Alarms sub-tab ============

const CAT_COLOR: Record<string, string> = {
  TRIP: "#ff453a", VOLTAGE_MISS: "#ff9500", COMM_LOST: "#a76bff",
  POWER_SUPPLY: "#e3350d", LUBRICATION: "#0aa5c7",
  SYSTEM: "#8e8e93", AUTH: "#8e8e93", OTHER: "#5a6d7c",
};
const UNIT_COLOR: Record<string, string> = {
  "UNIT 1": "#34c759", "UNIT 2": "#ff9500", "STAT": "#2481cc", "System": "#8e8e93",
};

// Open the shared per-second telemetry drill centered on an alarm's EXACT
// second (±45 s), so the event is visible with a marker — not lost in a 5-min
// window. telemetry_src (from the backend) says which dataset covers that
// moment; absent means no telemetry exists → the button isn't shown.
function jumpToTelemetry(ev: AlarmEvent) {
  if (!ev.telemetry_src) return;
  tg()?.HapticFeedback?.impactOccurred("light");
  window.dispatchEvent(new CustomEvent("combined:show-detail",
    { detail: { src: ev.telemetry_src, at: ev.ts } }));
}

export function AlarmsSub({ onErr }: { onErr: (m: string) => void }) {
  const { t } = useT();
  const catLabel = (k: string) => { const r = t("alarms.cat." + k); return r === "alarms.cat." + k ? k : r; };
  const [summary, setSummary] = useState<AlarmSummary | null>(null);
  const [board, setBoard] = useState<AlarmLeaderboard | null>(null);
  const [unit, setUnit] = useState<"" | "UNIT 1" | "UNIT 2" | "STAT">("");
  const [cat, setCat] = useState<string>("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [drill, setDrill] = useState<{ type_key: string; unit: string | null } | null>(null);

  useEffect(() => {
    setSummary(null);
    api.get<AlarmSummary>("/api/combined/alarms/summary").then(setSummary).catch((e: ApiError) => onErr(e.message));
  }, [onErr]);

  useEffect(() => {
    setBoard(null);
    const p = new URLSearchParams({ limit: "25" });
    if (unit) p.set("unit", unit);
    if (cat) p.set("category", cat);
    if (activeOnly) p.set("active_only", "true");
    api.get<AlarmLeaderboard>(`/api/combined/alarms/leaderboard?${p.toString()}`)
      .then(setBoard).catch((e: ApiError) => onErr(e.message));
  }, [unit, cat, activeOnly, onErr]);

  const maxN = useMemo(() => (board?.items ?? []).reduce((m, x) => Math.max(m, x.n), 0), [board]);

  return (
    <div>
      {/* KPI tiles */}
      <div className="auto-tiles" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
        <div className="auto-tile">
          <div className="auto-tile-top"><span className="auto-tile-icon">🚨</span>
            <span className="auto-tile-title">{t("alarms.total")}</span></div>
          <div className="auto-tile-bigrow">
            <span className="auto-tile-big">{summary ? summary.total.toLocaleString("ru-RU") : "—"}</span>
            <span className="auto-tile-unit">{t("alarms.records")}</span>
          </div>
        </div>
        <div className="auto-tile" style={summary && summary.active > 0 ? { border: "1.6px solid #ff453a" } : undefined}>
          <div className="auto-tile-top"><span className="auto-tile-icon">🔴</span>
            <span className="auto-tile-title">{t("alarms.active_now")}</span></div>
          <div className="auto-tile-bigrow">
            <span className="auto-tile-big" style={summary && summary.active > 0 ? { color: "#ff453a" } : undefined}>
              {summary ? summary.active : "—"}
            </span>
            <span className="auto-tile-unit">{t("alarms.records")}</span>
          </div>
        </div>
        <div className="auto-tile">
          <div className="auto-tile-top"><span className="auto-tile-icon">⚠️</span>
            <span className="auto-tile-title">{t("alarms.not_acked")}</span></div>
          <div className="auto-tile-bigrow">
            <span className="auto-tile-big">{summary ? summary.not_acked_active : "—"}</span>
            <span className="auto-tile-unit">{t("alarms.records")}</span>
          </div>
        </div>
      </div>

      {/* Split: by unit + by category */}
      {summary && (
        <div className="card" style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>{t("alarms.by_unit")}</div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 13 }}>
            {summary.by_unit.map((u) => (
              <div key={u.unit} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{
                  padding: "2px 8px", background: UNIT_COLOR[u.unit] || "var(--hint)",
                  color: "white", borderRadius: 6, fontWeight: 700, fontSize: 11,
                }}>{u.unit}</span>
                <span style={{ fontWeight: 700 }}>{u.n.toLocaleString("ru-RU")}</span>
                {u.active > 0 && <span style={{ color: "#ff453a", fontSize: 11 }}>· {u.active} 🔴</span>}
              </div>
            ))}
          </div>
          <div style={{ fontWeight: 700, marginTop: 12, marginBottom: 6 }}>{t("alarms.by_category")}</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", fontSize: 12 }}>
            {summary.by_category.map((c) => (
              <span key={c.category} style={{
                padding: "3px 10px", borderRadius: 10,
                background: (CAT_COLOR[c.category] || "#5a6d7c") + "22",
                color: CAT_COLOR[c.category] || "#5a6d7c",
                border: "1px solid " + (CAT_COLOR[c.category] || "#5a6d7c"),
              }}>
                {catLabel(c.category)} · <b>{c.n.toLocaleString("ru-RU")}</b>
              </span>
            ))}
          </div>
          <div style={{ fontSize: 11, color: "var(--hint)", marginTop: 10 }}>
            {t("alarms.period")}:{" "}
            <b style={{ color: "var(--text)", fontVariantNumeric: "tabular-nums" }}>{summary.d_from?.slice(0, 10) ?? "—"}</b>
            {" → "}
            <b style={{ color: "var(--text)", fontVariantNumeric: "tabular-nums" }}>{summary.d_to?.slice(0, 10) ?? "—"}</b>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", fontSize: 13 }}>
          <span style={{ color: "var(--hint)" }}>{t("alarms.filter_unit")}:</span>
          {(["", "UNIT 1", "UNIT 2", "STAT"] as const).map((u) => (
            <button key={u || "all"} onClick={() => { tg()?.HapticFeedback?.selectionChanged?.(); setUnit(u); }}
              style={{
                padding: "3px 10px", borderRadius: 8, border: "1px solid var(--border)",
                background: unit === u ? "var(--link, #2481cc)" : "transparent",
                color: unit === u ? "white" : "var(--text)", fontSize: 12, cursor: "pointer",
              }}>
              {u || t("alarms.all")}
            </button>
          ))}
          <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, marginLeft: 8 }}>
            <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
            {t("alarms.active_only")}
          </label>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", fontSize: 13, marginTop: 8 }}>
          <span style={{ color: "var(--hint)" }}>{t("alarms.filter_cat")}:</span>
          <button onClick={() => { tg()?.HapticFeedback?.selectionChanged?.(); setCat(""); }}
            style={{
              padding: "3px 10px", borderRadius: 8, border: "1px solid var(--border)",
              background: cat === "" ? "var(--link, #2481cc)" : "transparent",
              color: cat === "" ? "white" : "var(--text)", fontSize: 12, cursor: "pointer",
            }}>
            {t("alarms.all")}
          </button>
          {(summary?.by_category ?? []).map((c) => (
            <button key={c.category}
              onClick={() => { tg()?.HapticFeedback?.selectionChanged?.(); setCat(c.category); }}
              style={{
                padding: "3px 10px", borderRadius: 8, fontSize: 12, cursor: "pointer",
                border: "1px solid " + (CAT_COLOR[c.category] || "var(--border)"),
                background: cat === c.category ? (CAT_COLOR[c.category] || "#5a6d7c") : "transparent",
                color: cat === c.category ? "white" : (CAT_COLOR[c.category] || "var(--text)"),
              }}>
              {catLabel(c.category)}
            </button>
          ))}
        </div>
      </div>

      {/* Leaderboard */}
      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>{t("alarms.leaderboard")}</div>
        <div style={{ fontSize: 11, color: "var(--hint)", marginBottom: 8 }}>{t("alarms.leaderboard_hint")}</div>
        {!board && <div className="empty-state"><span className="spinner" /></div>}
        {board && board.items.length === 0 && <div className="empty-state">{t("common.no_data")}</div>}
        {board && board.items.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {board.items.map((it) => {
              const pct = maxN > 0 ? Math.max(6, Math.round((it.n / maxN) * 100)) : 6;
              const color = CAT_COLOR[it.category] || "#5a6d7c";
              return (
                <div key={it.type_key}
                  onClick={() => { tg()?.HapticFeedback?.impactOccurred("light"); setDrill({ type_key: it.type_key, unit: unit || null }); }}
                  style={{ cursor: "pointer" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                    <span style={{
                      minWidth: 108, textAlign: "center", padding: "1px 6px",
                      background: color, color: "white", borderRadius: 6, fontWeight: 700, fontSize: 10,
                      whiteSpace: "nowrap",
                    }}>{catLabel(it.category)}</span>
                    <span style={{ flex: 1, fontSize: 12, fontWeight: 600 }}>{it.type_key}</span>
                    <span style={{ fontWeight: 700, fontSize: 14, color }}>{it.n.toLocaleString("ru-RU")}</span>
                  </div>
                  <div style={{
                    height: 6, borderRadius: 6, background: "var(--bg2, rgba(255,255,255,0.08))", overflow: "hidden",
                  }}>
                    <div style={{
                      height: "100%", width: pct + "%",
                      background: `linear-gradient(90deg, ${color}, ${color}bb)`, borderRadius: 6,
                    }} />
                  </div>
                  <div style={{ marginTop: 3, fontSize: 11, color: "var(--hint)", display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {it.n_u1 > 0 && <span><b style={{ color: UNIT_COLOR["UNIT 1"] }}>U1</b> {it.n_u1}</span>}
                    {it.n_u2 > 0 && <span><b style={{ color: UNIT_COLOR["UNIT 2"] }}>U2</b> {it.n_u2}</span>}
                    {it.n_stat > 0 && <span><b style={{ color: UNIT_COLOR["STAT"] }}>STAT</b> {it.n_stat}</span>}
                    {it.n_active > 0 && <span style={{ color: "#ff453a" }}>🔴 {it.n_active} {t("alarms.active_short")}</span>}
                    {it.last_ts && (
                      <span>· {t("alarms.last")}:{" "}
                        <b style={{ color: "var(--text)", fontVariantNumeric: "tabular-nums" }}>{it.last_ts.slice(0, 10)}</b>{" "}
                        <span style={{ color: "var(--hint)", fontVariantNumeric: "tabular-nums" }}>{it.last_ts.slice(11, 16)}</span>
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {drill && (
        <AlarmTimelineModal type_key={drill.type_key} unit={drill.unit}
          onClose={() => setDrill(null)} />
      )}
    </div>
  );
}

// Every occurrence of one alarm type, newest first, with a jump to the SCADA
// telemetry drill for that timestamp (so operators can see what the voltage was
// doing right around it).
function AlarmTimelineModal({ type_key, unit, onClose }: {
  type_key: string; unit: string | null; onClose: () => void;
}) {
  const { t } = useT();
  const [data, setData] = useState<AlarmTimeline | null>(null);
  const [path, setPath] = useState<string[]>([]); // drill: [] → month → day → events
  const stepBack = () => setPath((pp) => (pp.length ? pp.slice(0, -1) : pp));
  // Bind native back ONCE; a ref keeps it reading the CURRENT path/onClose so
  // it always steps up exactly one level (never jumps back to the very start).
  const backRef = useRef<() => void>(() => onClose());
  backRef.current = () => { if (path.length) stepBack(); else onClose(); };
  useTelegramBackButton(() => backRef.current(), []);
  useEffect(() => {
    setPath([]);
    const p = new URLSearchParams({ type_key, limit: "300" });
    if (unit) p.set("unit", unit);
    api.get<AlarmTimeline>(`/api/combined/alarms/timeline?${p.toString()}`)
      .then(setData).catch(() => undefined);
  }, [type_key, unit]);

  const openTelemetry = (ev: AlarmEvent) => {
    jumpToTelemetry(ev);
    onClose();
  };

  // Only occurrences where the station was actually running (non-zero
  // telemetry). Zero/no-telemetry entries are nothing to look at → hidden.
  const shown = (data?.events ?? []).filter((e) => e.has_data);
  const total = data?.events.length ?? 0;

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 200,
      display: "flex", alignItems: "flex-end",
    }} onClick={onClose}>
      <div style={{
        background: "var(--bg)", width: "100%", maxHeight: "92vh", overflowY: "auto",
        borderRadius: "16px 16px 0 0", padding: 16,
      }} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginTop: 0, fontSize: 15 }}>{type_key}</h2>
        <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 10 }}>
          {data ? `${shown.length} ${t("alarms.occurrences")}` : "…"}
          {data && shown.length < total && <> · {t("alarms.of_total").replace("{n}", String(total))}</>}
          {unit && <> · {unit}</>}
        </div>
        {!data && <div className="empty-state"><span className="spinner" /></div>}
        {data && shown.length === 0 && <div className="empty-state">{t("alarms.none_with_data")}</div>}
        {data && shown.length > 0 && (() => {
          const GRAN = [7, 10]; // slice lengths: YYYY-MM (month), YYYY-MM-DD (day)
          const start = new Set(shown.map((e) => e.ts.slice(0, 7))).size > 1 ? 0 : 1;
          const filtered = shown.filter((ev) => path.every((v, i) => ev.ts.slice(0, GRAN[start + i]) === v));
          const gi = start + path.length;
          const isLeaf = gi >= GRAN.length;
          const groups = (() => {
            if (isLeaf) return [] as { key: string; count: number; active: number }[];
            const m = new Map<string, { key: string; count: number; active: number }>();
            for (const ev of filtered) {
              const k = ev.ts.slice(0, GRAN[gi]);
              const g = m.get(k) ?? { key: k, count: 0, active: 0 };
              g.count++; if (ev.active) g.active++;
              m.set(k, g);
            }
            return [...m.values()].sort((a, b) => (a.key < b.key ? 1 : -1));
          })();
          return (
            <>
              {/* breadcrumb across the drill levels */}
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 8, fontSize: 12 }}>
                {path.length > 0 && (
                  <button onClick={() => { tg()?.HapticFeedback?.impactOccurred("light"); stepBack(); }} style={{
                    background: "var(--bg2)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6,
                    padding: "2px 9px", cursor: "pointer", fontWeight: 700, color: "var(--text)",
                  }}>‹ {t("auto.back")}</button>
                )}
                <button onClick={() => setPath([])} style={{
                  background: "none", border: "none", padding: 0, cursor: "pointer", fontWeight: 700,
                  color: path.length ? "var(--link, #2481cc)" : "var(--text)",
                }}>{t("alarms.all_periods")}</button>
                {path.map((p, i) => (
                  <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ color: "var(--hint)" }}>›</span>
                    <button onClick={() => setPath(path.slice(0, i + 1))} style={{
                      background: "none", border: "none", padding: 0, cursor: "pointer", fontWeight: 700,
                      fontVariantNumeric: "tabular-nums",
                      color: i === path.length - 1 ? "var(--text)" : "var(--link, #2481cc)",
                    }}>{p}</button>
                  </span>
                ))}
                <span style={{ flex: 1 }} />
                <span style={{ color: "var(--hint)" }}>{filtered.length} {t("alarms.occurrences")}</span>
              </div>

              {/* group ("разрез") level — click to drill in */}
              {!isLeaf && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {groups.map((g) => (
                    <button key={g.key} onClick={() => { tg()?.HapticFeedback?.impactOccurred("light"); setPath([...path, g.key]); }}
                      style={{
                        display: "flex", alignItems: "center", gap: 12, textAlign: "left", width: "100%",
                        padding: "13px 14px", borderRadius: 14, cursor: "pointer", color: "var(--text)",
                        background: "var(--bg2)",
                        border: "1px solid " + (g.active > 0 ? "rgba(255,69,58,0.45)" : "rgba(255,255,255,0.10)"),
                      }}>
                      <span style={{
                        width: 9, height: 9, borderRadius: "50%", flexShrink: 0,
                        background: g.active > 0 ? "#ff453a" : "#2481cc",
                        boxShadow: g.active > 0 ? "0 0 8px #ff453a" : "0 0 6px rgba(36,129,204,0.7)",
                      }} />
                      <span style={{ fontWeight: 800, fontVariantNumeric: "tabular-nums", fontSize: 15, letterSpacing: 0.3 }}>{g.key}</span>
                      <span style={{ flex: 1 }} />
                      {g.active > 0 && <span style={{
                        color: "#ff453a", fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 20,
                        background: "rgba(255,69,58,0.14)", border: "1px solid rgba(255,69,58,0.4)",
                      }}>{g.active} ACTIVE</span>}
                      <span style={{
                        fontSize: 12.5, fontWeight: 700, color: "var(--text)", padding: "3px 10px", borderRadius: 20,
                        background: "rgba(127,127,127,0.16)", fontVariantNumeric: "tabular-nums",
                      }}>{g.count}</span>
                      <span style={{ color: "var(--hint)", fontSize: 17, fontWeight: 700, lineHeight: 1 }}>›</span>
                    </button>
                  ))}
                </div>
              )}

              {/* leaf — the actual occurrences */}
              {isLeaf && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {filtered.map((ev, i) => (
                    <div key={i} style={{
                      padding: "8px 10px", borderRadius: 8,
                      display: "flex", flexDirection: "column", gap: 4,
                      background: ev.active ? "rgba(255,69,58,0.12)" : "rgba(255,196,0,0.10)",
                      border: ev.active ? "1px solid #ff453a" : "1px solid rgba(255,196,0,0.30)",
                      borderLeft: ev.active ? "3px solid #ff453a" : "3px solid #ffc400",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{
                          minWidth: 50, textAlign: "center", padding: "1px 6px",
                          background: UNIT_COLOR[ev.unit] || "var(--hint)", color: "white",
                          borderRadius: 5, fontWeight: 700, fontSize: 10, whiteSpace: "nowrap",
                        }}>{ev.unit}</span>
                        <DateTimeTag ts={ev.ts} />
                        <span style={{ flex: 1 }} />
                        {ev.active && <span style={{ color: "#ff453a", fontSize: 11, fontWeight: 700 }}>ACTIVE</span>}
                        {ev.telemetry_src && (
                          <button className="btn secondary" style={{ padding: "3px 8px", fontSize: 11 }}
                            onClick={() => openTelemetry(ev)}>
                            📈 {ev.telemetry_src}
                          </button>
                        )}
                      </div>
                      {ev.plc_tag && (
                        <div style={{ fontSize: 10, color: "var(--hint)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginLeft: 58 }}>
                          {ev.plc_tag}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          );
        })()}
        <button className="btn" style={{ marginTop: 12 }} onClick={onClose}>{t("common.close")}</button>
      </div>
    </div>
  );
}
